import {
  Button,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Flex,
  Modal,
  Row,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  message,
  usePaypingContext,
} from "@payping/web-components";
import { useToggle } from "react-use";

import { toJalali } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils";
import useTranslator from "@/shared/translate/translator";
import { JALALI_DATE_TIME_FORMAT } from "@/shared/constants/date.constant";
import { RefundOperationStateEnum } from "@/refund/repository/refund/v1/refund-v1-api";
import { REFUND_STATE_COLOR_DICTIONARY, WAGE_PAYER_DICTIONARY } from "@/refund/constants/dictionary.constant";
import {
  useCancelRefund,
  useGetRefundInfoWithCode,
  useGetWalletBalance,
  useReinitializeRefund,
} from "@/refund/use-cases";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { RefetchOptions } from "@tanstack/react-query";

type Props = {
  code: string | null;
  onCloseClicked: () => void;
  refetchList?: (options?: RefetchOptions) => Promise<any>;
};

const RefundTransactionDetails: React.FC<Props> = ({ code, onCloseClicked, refetchList }) => {
  const { t } = useTranslator("refund.transactions.details");
  const { data, isLoading, refetch } = useGetRefundInfoWithCode(code ?? "");
  const cancelRefund = useCancelRefund();
  const reinitializeRefund = useReinitializeRefund();
  const walletBalance = useGetWalletBalance();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle(false);

  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const isOpen = Boolean(code);

  const handleMenuClick = () => {
    toggleConfirmModal();
  };

  const handleCancel = async () => {
    if (code) {
      try {
        await cancelRefund.mutateAsync(code);
        message.success({ content: t("actions.cancelRefundSuccess") });
        await refetch();
        await refetchList?.();
        toggleConfirmModal();
      } catch {
        message.error({ content: t("actions.cancelRefundError") });
      }
    }
  };

  const handleReinitialize = async () => {
    if (code) {
      try {
        await reinitializeRefund.mutateAsync(code);
        message.success({ content: t("actions.reinitializeRefundSuccess") });
        await refetch();
        await refetchList?.();
        await walletBalance.refetch();
      } catch {
        message.error({ content: t("actions.reinitializeRefundError") });
      }
    }
  };

  const menuItems = [
    {
      key: "cancel",
      label: t("actions.cancelRefund"),
      onClick: handleMenuClick,
    },
    {
      key: "reinitialize",
      label: t("actions.reinitializeRefund"),
      onClick: handleReinitialize,
    },
  ];
  const getTooltipTitle = (state?: number) => {
    switch (state) {
      case RefundOperationStateEnum.Cancelled:
        return t("tooltips.refund_canceled");
      case RefundOperationStateEnum.ProcessingRefund:
        return t("tooltips.sent_to_bank");
      case RefundOperationStateEnum.RefundFailed:
        return t("tooltips.refund_failed");
      case RefundOperationStateEnum.RefundSucceeded:
        return t("tooltips.refund_completed");
      default:
        return undefined;
    }
  };

  const disabledStates = [
    RefundOperationStateEnum.Cancelled,
    RefundOperationStateEnum.Created,
    RefundOperationStateEnum.ProcessingRefund,
    RefundOperationStateEnum.RefundFailed,
    RefundOperationStateEnum.RefundSucceeded,
  ];

  const loading = cancelRefund.isPending || reinitializeRefund.isPending;

  if (isOpen)
    return (
      <>
        <Drawer
          open={isOpen}
          extra={
            data ? (
              <Tooltip title={getTooltipTitle(data.state)}>
                <span>
                  <Dropdown
                    menu={{ items: menuItems }}
                    trigger={["click"]}
                    disabled={disabledStates.includes(data.state ?? -1)}
                  >
                    <Button type="default" disabled={disabledStates.includes(data.state ?? -1)} loading={loading}>
                      {t("actions.title")}
                    </Button>
                  </Dropdown>
                </span>
              </Tooltip>
            ) : null
          }
          title={t("title")}
          placement={md ? "left" : "bottom"}
          onClose={onCloseClicked}
          height={"100%"}
          width={md ? 528 : "100%"}
        >
          {isLoading ? (
            <Skeleton paragraph loading={isLoading} />
          ) : data ? (
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Descriptions
                  className="tw:w-full"
                  column={1}
                  bordered
                  title={
                    <Flex wrap align="center" gap={"small"}>
                      <Typography.Text>{t("subtitle")}</Typography.Text>
                      <Tag color={data.state ? REFUND_STATE_COLOR_DICTIONARY[data.state] : "default"}>
                        {data.localizedState}
                      </Tag>
                    </Flex>
                  }
                >
                  <Descriptions.Item label={t("amount")}>
                    <Flex align="center">
                      <Typography.Text type="secondary">{`${formatLocalizedNumber({ value: data.requestedAmount ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Descriptions.Item>

                  <Descriptions.Item label={t("transferReference")}>{data.transferReference || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("creationDateTime")}>
                    {toJalali({
                      date: getCorrectDate(data.creationDateTime ?? ""),
                      toFormat: JALALI_DATE_TIME_FORMAT,
                    }).string || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("successDateTime")}>
                    {toJalali({ date: getCorrectDate(data.successDateTime ?? ""), toFormat: JALALI_DATE_TIME_FORMAT })
                      .string || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("code")}>{data.code}</Descriptions.Item>
                  <Descriptions.Item label={t("paymentCode")}>{data.paymentCode ?? ""}</Descriptions.Item>
                  <Descriptions.Item label={t("refundWage")}>
                    <Flex align="center">
                      <Typography.Text type="secondary">
                        {formatLocalizedNumber({
                          value: data.refundWage ?? 0,
                          options: { maximumFractionDigits: 2 },
                        })}
                      </Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("wagePayer")}>
                    {data.wagePayer ? t(WAGE_PAYER_DICTIONARY[data.wagePayer]) : ""}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          ) : null}
        </Drawer>

        <Modal
          open={isConfirmModalOpen}
          title={t("actions.confirmModal.title")}
          onOk={handleCancel}
          onCancel={toggleConfirmModal}
          okText={t("actions.confirmModal.okText")}
          cancelText={t("actions.confirmModal.cancelText")}
          confirmLoading={cancelRefund.isPending}
        >
          <p>{t("actions.confirmModal.content")}</p>
        </Modal>
      </>
    );
  return "";
};

export default RefundTransactionDetails;
